// Import depuis les CDN (disponibles globalement)
// import { PDFFont } from "pdf-lib";

// Types globaux définis dans src/types/global.d.ts
import {
  FormattedLine,
  TextFormattingOptions,
  TextSegment,
  WrappedLine,
} from "../types/pdf-generator";

class TextParser {
  trace: boolean = false;
  id: string = "";
  private defaultFont: any;

  constructor(defaultFont: any = null) {
    this.defaultFont = defaultFont;
  }

  setId(id: string): void {
    this.id = id;
  }

  setTrace(trace: boolean): void {
    this.trace = trace;
  }

  /**
   * Parse HTML et convertit en instructions PDF
   */
  parseHtmlContent(htmlText: string): string {
    // Nettoyer le HTML
    let text = htmlText || "";

    // Parser les listes ordonnées et non ordonnées avec classes spécifiques
    text = this.parseHtmlLists(text);

    // Parser les autres éléments
    text = this.parseHtmlElements(text);

    if (this.trace) {
      console.log("parse HtmlContent result:", text);
    }

    return text;
  }

  /**
   * Parse les listes HTML avec classes TDA - Version simplifiée
   */
  private parseHtmlLists(text: string): string {
    let result = text;

    const error = this.controlBalise(text);
    if (error) {
      console.warn("Error in rule " + this.id, "text:", text);
      // throw new Error("Error in rule " + this.id);
    }

    // 1.a Traiter les listes ruleL1 (tirets)
    // 1.b Traiter les listes simples
    result = result.replace(
      /<ul\s+class=['"]ruleL1['"][^>]*>(.*?)<\/ul>/gis,
      (match, content) => {
        return this.convertSimpleList(content, "ruleL1");
      }
    );

    result = result.replace(/<ul[^>]*>(.*?)<\/ul>/gis, (match, content) => {
      return this.convertSimpleList(content, "ul");
    });

    // 2. Traiter les listes ruleL4 (a, b, c) en premier (plus spécifiques)
    result = result.replace(
      /<ol\s+class=['"]ruleL4['"][^>]*>(.*?)<\/ol>/gis,
      (match, content) => {
        return this.convertSimpleList(content, "ruleL4");
      }
    );

    // 3. Traiter les listes ruleL3 (1, 2, 3)
    result = result.replace(
      /<ol\s+class=['"]ruleL3['"][^>]*>(.*?)<\/ol>/gis,
      (match, content) => {
        if (this.trace) {
          console.log("parseHtmlLists ruleL3", content);
        }
        return this.convertSimpleList(content, "ruleL3");
      }
    );

    // 4. Traiter les listes ruleL2 (A, B, C)
    result = result.replace(
      /<ol\s+class=['"]ruleL2['"][^>]*>(.*?)<\/ol>/gis,
      (match, content) => {
        return this.convertSimpleList(content, "ruleL2");
      }
    );

    result = result.replace(/<ol[^>]*>(.*?)<\/ol>/gis, (match, content) => {
      return this.convertSimpleList(content, "ol");
    });

    return result;
  }

  private controlBalise(content: string) {
    const balises = [
      {
        tag: "li",
        open: /<li[^>]*>/gi,
        close: /<\/li>/gi,
      },
      {
        tag: "ol",
        open: /<ol[^>]*>/gi,
        close: /<\/ol>/gi,
      },
      {
        tag: "ul",
        open: /<ul[^>]*>/gi,
        close: /<\/ul>/gi,
      },
      {
        tag: "span",
        open: /<span[^>]*>/gi,
        close: /<\/span>/gi,
      },
    ];

    let error = false;
    for (const balise of balises) {
      // Count the number of opening and closing tags
      const open = (content.match(balise.open) || []).length;
      const close = (content.match(balise.close) || []).length;
      if (open != close) {
        console.log(
          "rule ",
          this.id,
          "error",
          balise.tag,
          "open",
          open,
          "close",
          close
        );
        error = true;
      }
    }

    return error;
  }

  /**
   * Convertit une liste simple en préservant l'ordre du contenu
   */
  private convertSimpleList(content: string, listType: string): string {
    let result = "\n";

    // Approche séquentielle : découper le contenu en tokens dans l'ordre
    const tokens: string[] = [];

    // Utiliser une approche différente : split sur <li> puis reconstruire
    const parts = content.split(/(<li[^>]*>.*?<\/li>)/gis);

    if (this.trace) {
      console.log("convertSimpleList type:", listType, "content:", content);
      console.log("convertSimpleList - parts:", parts);
    }

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      if (part.trim()) {
        if (part.match(/^<li[^>]*>/i)) {
          // C'est un élément <li>
          if (this.trace) {
            console.log("part LI>", part);
          }
          tokens.push(part);
        } else {
          // C'est du texte libre - le garder en un seul bloc
          if (this.trace) {
            console.log("part:", part);
          }
          tokens.push(part);
        }
      }
    }

    if (this.trace) {
      console.log("convertSimpleList", `'${listType}'`, " tokens:", tokens);
    }

    let liIndex = 0; // Compteur pour les éléments de liste

    let indent = "";
    let paddingTop = 0;
    let paddingBottom = 0;
    switch (listType) {
      case "ruleL2": // A, B, C
        indent = "~6"; //  3 espaces
        paddingTop = 3;
        paddingBottom = 6;
        break;
      case "ruleL3": // 1, 2, 3
        indent = "~10"; // 6 espaces (plus indenté)
        paddingTop = 1;
        paddingBottom = 3;
        break;
      case "ruleL4": // a, b, c
        indent = "~14"; // 9 espaces (encore plus indenté)
        break;
      case "ruleL1":
        indent = "~6"; // 6 espaces
        paddingTop = 1;
        paddingBottom = 2;
        break;
      default:
        indent = "~5"; // 5 espaces
    }

    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i];

      // Si c'est un élément <li>
      if (token.match(/<li[^>]*>.*?<\/li>/is)) {
        // Extraire le contenu du <li>
        let itemContent = token.replace(/<li[^>]*>(.*?)<\/li>/is, "$1").trim();

        // Traiter le HTML dans le contenu de l'élément de liste
        itemContent = this.parseHtmlElements(itemContent);
        // remplace '\n' par '\n'+indent+"|"
        itemContent = itemContent.replace(/\n/g, "\n" + indent.trim() + "|");

        // Générer la puce
        let bullet = "";

        // Détecter les classes spéciales
        if (
          token.includes("class='floor'") ||
          token.includes('class="floor"')
        ) {
          bullet = "➜ ";
        } else if (token.includes("class='player'")) {
          bullet = "⚠ ";
        } else {
          // Générer selon le type
          switch (listType) {
            case "ruleL2":
              bullet = String.fromCharCode(65 + liIndex) + ") ";
              break;
            case "ruleL3":
              bullet = liIndex + 1 + ") ";
              break;
            case "ruleL4":
              bullet = String.fromCharCode(97 + liIndex) + ") ";
              break;
            case "ruleL1":
              bullet = "- ";
              break;
            default:
              bullet = "• ";
          }
        }

        if (paddingTop > 0) {
          result += "~~pt:" + paddingTop + "\n";
        }
        result += `${indent})${bullet}${itemContent}\n`;
        liIndex++; // Incrémenter le compteur pour la prochaine puce
      }
      // Si c'est du texte libre (bloc complet maintenant)
      else {
        // Le texte libre peut contenir du HTML, il faut le traiter
        const processedText = this.parseHtmlElements(token);
        const freeText = processedText.trim();
        if (freeText) {
          // Ajouter le texte libre avec un saut de ligne avant et après
          if (freeText.startsWith("~")) {
            result += `${freeText}\n`;
          } else {
            result += `${indent}|${freeText}\n`;
          }
        }
      }

      if (paddingBottom > 0) {
        result += "~~pb:" + paddingBottom + "\n";
      }
    }

    return result + "\n";
  }

  /**
   * Découpe le texte en lignes avec gestion des listes
   */
  wrapText(text: string): WrappedLine[] {
    const lines: WrappedLine[] = [];

    // Séparer par les sauts de ligne pour préserver la structure des listes
    const paragraphs = text.split("\n");

    if (this.trace && paragraphs.length > 1) {
      console.log(
        "wrapText ",
        this.id,
        " count:",
        paragraphs.length,
        "text:",
        paragraphs
      );
    }

    let nextPaddingTop = 0;

    paragraphs.forEach((paragraph) => {
      if (!paragraph.trim()) {
        return;
      }

      // Détecter si c'est une ligne de liste (commence par ~)
      const isIndent = paragraph.startsWith("~");

      let match: RegExpMatchArray | null = null;
      let indent = 0;
      let firstIndent = 0;
      let isListItem = false;
      let content = paragraph;
      let paddingTop = nextPaddingTop;

      if (isIndent) {
        // format: '~12)*' => list (avec un ou 2 digit)
        // format: '~12|*' => suite du texte
        // format: '~~pt:12' => padding top
        // format: '~~pb:12' => padding bottom
        do {
          if (content.startsWith("~~pt:")) {
            nextPaddingTop = parseInt(content.substring(5));
            match = null;
            indent = 0;
            return;
          }
          if (content.startsWith("~~pb:")) {
            let paddingBottom = parseInt(content.substring(5));
            if (lines.length > 0) {
              lines[lines.length - 1].paddingBottom = paddingBottom;
            }
            match = null;
            indent = 0;
            return;
          }

          match = content.match(/^~(\d+)\)(.*)/);
          if (match) {
            isListItem = true;
          } else {
            // format: '~3|*' => texte décalé
            match = content.match(/^~(\d+)\|(.*)/);
          }

          if (match) {
            indent = Math.max(indent, parseInt(match[1]));
            content = match[2];
          }
        } while (match && content.startsWith("~"));

        if (indent > 0) {
          if (isListItem) {
            firstIndent = Math.max(0, indent - 5);
          } else {
            firstIndent = indent;
          }
        }
      }

      lines.push({
        content,
        indent,
        firstIndent,
        paddingTop,
        paddingBottom: 0,
      });
      nextPaddingTop = 0;
    });

    if (this.trace) {
      console.log(
        "wrapText ",
        this.id,
        " count:",
        lines.length,
        "lines:",
        lines
      );
    }

    return lines;
  }

  /**
   * Parse les autres éléments HTML avec gestion améliorée des chevauchements
   */
  parseHtmlElements(text: string, withSpecialSearch: boolean = false): string {
    // Supprimer les balises de formatage et les remplacer par des marqueurs

    // saut de ligne
    text = text.replace(/<br\s*\/?>\s*/gi, "\n");
    text = text.replace(/<\/?code[^>]*>/gi, "");

    if (withSpecialSearch) {
      // Traiter les cas complexes de chevauchement d'abord
      // IMPORTANT: Traiter d'abord les cas les plus spécifiques (avec texte après)
      // puis les cas plus généraux (sans texte après)

      // Cas 1a: <span class='exple'><b>texte</b> autre_texte</span> (avec texte après)
      text = text.replace(
        /<span\s+class=['"]exple['"][^>]*><b[^>]*>(.*?)<\/b>\s+([^<]+)<\/span>/gis,
        " _**$1** $2_ "
      );

      // Cas 1b: <span class='exple'><b>texte</b></span> (sans texte après, ou juste des espaces)
      text = text.replace(
        /<span\s+class=['"]exple['"][^>]*><b[^>]*>(.*?)<\/b>\s*<\/span>/gis,
        " _**$1**_ "
      );

      // Cas 2: <b><span class='exple'>texte</span></b>
      text = text.replace(
        /<b[^>]*><span\s+class=['"]exple['"][^>]*>(.*?)<\/span><\/b>/gis,
        " **_$1_** "
      );

      // Cas 3a: <span class='exple2'><b>texte</b> autre_texte</span>
      text = text.replace(
        /<span\s+class=['"]exple2['"][^>]*><b[^>]*>(.*?)<\/b>\s+([^<]+)<\/span>/gis,
        " **_$1 $2_** "
      );

      // Cas 3b: <span class='exple2'><b>texte</b></span> (sans texte après)
      text = text.replace(
        /<span\s+class=['"]exple2['"][^>]*><b[^>]*>(.*?)<\/b>\s*<\/span>/gis,
        " **_$1_** "
      );

      // Cas 4: <b><span class='exple2'>texte</span></b>
      text = text.replace(
        /<b[^>]*><span\s+class=['"]exple2['"][^>]*>(.*?)<\/span><\/b>/gis,
        " **_$1_** "
      );

      // Cas 5a: <span class='exple'>texte</span> <b>autre</b> (avec espace entre)
      text = text.replace(
        /<span\s+class=['"]exple['"][^>]*>(.*?)<\/span>\s+<b[^>]*>(.*?)<\/b>/gis,
        " _$1_ **$2**"
      );
    }

    // Gras et italique (traitement normal)
    text = text.replace(/<b[^>]*>(.*?)<\/b>/gis, " **$1** ");
    text = text.replace(/<strong[^>]*>(.*?)<\/strong>/gis, " **$1** ");
    text = text.replace(/<i[^>]*>(.*?)<\/i>/gis, " *$1* ");
    text = text.replace(/<em[^>]*>(.*?)<\/em>/gis, " *$1* ");

    // Spans avec classes spéciales
    text = text.replace(
      /<span\s+class=['"]exple['"][^>]*>([^<]*?)<\/span>([^\s<])/gis,
      " _$1_ $2"
    );

    // Cas général: <span class='exple'>texte</span>
    text = text.replace(
      /<span\s+class=['"]exple['"][^>]*>(.*?)<\/span>/gis,
      " _$1_ "
    );
    text = text.replace(
      /<span\s+class=['"]exple2['"][^>]*>(.*?)<\/span>/gis,
      " **_$1_** "
    );

    // Gérer les liens avec spans complexes
    text = text.replace(/<alt\\_span>.*?<\/alt\\_span>/gis, "");

    // Cas simples: spans avec data-id sans alt_span
    text = text.replace(
      /<span[^>]*data-id='([^']*)'[^>]*>(.*?)<\/span>/gis,
      " {$1|**$2**} "
    );
    text = text.replace(
      /\(([^)]*<span[^>]*data-id='([^']*)'[^>]*>.*?<\/span>[^)]*)\)/gis,
      " {($2|$1)} "
    );

    // Supprimer les autres spans mais préserver le contenu
    text = text.replace(/<span[^>]*>(.*?)<\/span>/gis, "$1");

    // Sauts de ligne
    text = text.replace(/<\/p>/gi, "\n\n");

    // Supprimer les autres balises HTML mais préserver le contenu
    text = text.replace(/<[^>]+>/g, "");

    // Nettoyer les espaces multiples mais préserver la ponctuation
    text = text.replace(/\n{3,}/g, "\n\n");
    text = text.replace(/[ \t]{2,}/g, " ");

    // Corriger les espaces autour de la ponctuation
    text = text.replace(/\s+([,.;:!?)])/g, "$1");
    text = text.replace(/\(\s+/g, "(");
    text = text.replace(/\s+\)/g, ")");

    return text.trimEnd();
  }

  /**
   * Supprimer les ancrages pour les liens vers les exemples
   */
  removeAnchors(text: string): string {
    let result = text;

    // Pattern pour détecter les références aux exemples avec inline_link_ia
    // Gère les cas avec et sans parenthèses autour
    const patterns = [
      // Pattern 1: (voir exemple) ou (Voir exemple) - avec parenthèses
      /\(\s*<span[^>]*data-id='[^']*'[^>]*class=['"][^'"]*inline_link_ia[^'"]*['"][^>]*>.*?<\/span>\s*\)/gis,

      // Pattern 2: voir exemple sans parenthèses
      /<span[^>]*data-id='[^']*'[^>]*class=['"][^'"]*inline_link_ia[^'"]*['"][^>]*>.*?<\/span>/gis,

      // Pattern 3: cas où il y a du texte avant ou après le span
      /([^>]*)\s*<span[^>]*data-id='[^']*'[^>]*class=['"][^'"]*inline_link_ia[^'"]*['"][^>]*>.*?<\/span>\s*([^<]*)/gis,
    ];

    // Appliquer les patterns de remplacement
    patterns.forEach((pattern, index) => {
      if (index === 0) {
        // Pattern 1: supprimer complètement les parenthèses et le contenu
        result = result.replace(pattern, "");
      } else if (index === 1) {
        // Pattern 2: supprimer juste le span
        result = result.replace(pattern, "");
      } else if (index === 2) {
        // Pattern 3: gérer le cas avec du texte avant/après
        result = result.replace(pattern, (match, before, after) => {
          // Si le texte avant se termine par une parenthèse ouvrante et le texte après commence par une parenthèse fermante
          if (before.trim().endsWith("(") && after.trim().startsWith(")")) {
            return before.trim().slice(0, -1) + after.trim().slice(1);
          }
          // Sinon, garder le texte avant et après
          return (before + after).trim();
        });
      }
    });

    // Nettoyer les ponctuations superflues après suppression
    if (result !== text) {
      // Supprimer les espaces multiples
      result = result.replace(/\s+/g, " ");

      // Nettoyer les ponctuations doubles
      result = result.replace(/\.\.+/g, ".");
      result = result.replace(/,\s*,/g, ",");
      result = result.replace(/;\s*;/g, ";");

      // Nettoyer les espaces avant la ponctuation
      result = result.replace(/\s+([.,;:!?)])/g, "$1");

      // Nettoyer les espaces après les parenthèses ouvrantes
      result = result.replace(/\(\s+/g, "(");

      // Nettoyer les espaces avant les parenthèses fermantes
      result = result.replace(/\s+\)/g, ")");

      // Supprimer les espaces en début et fin
      result = result.trim();

      if (this.trace) {
        console.log("removeAnchors", text, "=>", result);
      }
    }

    return result;
  }

  /**
   * Dessine du texte avec formatage (gras, italique, souligné) - Version simple
   */
  calculateFormattedLine(
    wrappedLine: WrappedLine,
    options: TextFormattingOptions
  ): FormattedLine {
    const { fontSize, fonts, maxWidth, lineSpacing, textAlign, shiftSize } =
      options;

    // Diviser le texte en segments avec formatage
    let segments = this.parseTextFormatting(wrappedLine.content);

    const spaceSize =
      fonts.regular.widthOfTextAtSize(" ", fontSize) * shiftSize;

    let currentX = wrappedLine.firstIndent * spaceSize;
    let currentHeight = 0;

    segments.forEach((segment) => {
      const { text } = segment;
      // Nettoyer le texte du segment pour éviter les caractères problématiques
      segment.text = text.replace(/[\n\r\t]/g, " ").replace(/  /g, " ");
    });

    // Gérer les segments non séparables
    let startNonSeparable: number | false = false;

    for (let s = 0; s < segments.length; s++) {
      const segment = segments[s];

      if (!segment.text) {
        if (this.trace) {
          console.log("empty segment, rule", this.id, segments);
        }
        continue;
      }
      const { bold, italic, graphic, nonSeparable } = segment;

      if (nonSeparable) {
        if (startNonSeparable === false) {
          startNonSeparable = s;
          if (this.trace) {
            console.log(
              "nonSeparable: start group of segments from",
              segment.text
            );
          }
        }
      } else {
        startNonSeparable = false;
      }

      // Choisir la police selon le formatage (4 combinaisons possibles)
      let font: any; // Changed from PDFFont to any
      if (graphic) {
        // Utiliser la police graphique depuis les options
        font = fonts.graphic;
        // 'text' contient le code unicode sous forme de chaîne, on le convertit en nombre puis en caractère
        const unicode = parseInt(segment.text.substring(2), 16);
        const char = String.fromCharCode(unicode);
        segment.text = char;
      } else if (bold && italic) {
        font = fonts.boldItalic;
      } else if (bold) {
        font = fonts.bold;
      } else if (italic) {
        font = fonts.italic;
      } else {
        font = fonts.regular;
      }

      // Calculer la largeur du segment
      const segmentWidth = font.widthOfTextAtSize(segment.text, fontSize);

      // Vérifier si on dépasse la largeur max (saut de ligne simple)
      if (currentX + segmentWidth > maxWidth) {
        currentX = wrappedLine.indent * spaceSize;
        currentHeight += fontSize * lineSpacing;

        // segment non séparable: on va déplacer le groupe de segments non séparables
        if (nonSeparable && startNonSeparable && startNonSeparable < s) {
          if (this.trace) {
            console.log(
              "nonSeparable: move group of segments from",
              startNonSeparable,
              "to",
              s,
              "segments:",
              segments.slice(startNonSeparable, s)
            );
          }
          for (let i = startNonSeparable; i < s; i++) {
            segments[i].x = currentX;
            segments[i].y = currentHeight;
            currentX += segments[i].width || 0;
          }
        }
      }

      segment.x = currentX;
      segment.y = currentHeight;
      segment.font = font;
      segment.size = fontSize;
      segment.width = segmentWidth;

      // Avancer la position X
      currentX += segmentWidth;
    }

    // Traitement de l'alignement
    if (segments.length > 1 && textAlign !== "left") {
      // Retirer les segments vides
      segments = segments.filter(
        (segment) => segment.text && segment.text.trim().length > 0
      );

      // Aligner les segments
      if (textAlign === "center") {
        // Regrouper les segments par ligne (même valeur Y)
        const lineGroups: { [key: number]: TextSegment[] } = {};

        segments.forEach((segment) => {
          const y = segment.y || 0;
          if (!lineGroups[y]) {
            lineGroups[y] = [];
          }
          lineGroups[y].push(segment);
        });

        // Centrer chaque ligne séparément
        Object.values(lineGroups).forEach((lineSegments) => {
          const totalWidth = lineSegments.reduce(
            (acc, segment) => acc + (segment.width || 0),
            0
          );
          const margin = (maxWidth - totalWidth) / 2;
          lineSegments.forEach((segment) => {
            segment.x = (segment.x || 0) + margin;
          });
        });
      } else if (textAlign === "right") {
        // Regrouper les segments par ligne (même valeur Y)
        const lineGroups: { [key: number]: TextSegment[] } = {};

        segments.forEach((segment) => {
          const y = segment.y || 0;
          if (!lineGroups[y]) {
            lineGroups[y] = [];
          }
          lineGroups[y].push(segment);
        });

        // Aligner à droite chaque ligne séparément
        Object.values(lineGroups).forEach((lineSegments) => {
          const totalWidth = lineSegments.reduce(
            (acc, segment) => acc + (segment.width || 0),
            0
          );
          const startX = maxWidth - totalWidth;
          let currentX = startX;

          lineSegments.forEach((segment) => {
            segment.x = currentX;
            currentX += segment.width || 0;
          });
        });
      } else if (textAlign === "justify") {
        // Justification
        let currentY = segments[0].y || 0;
        let currentX = segments[0].x || 0;

        if (this.trace) {
          console.log(
            "align to:",
            textAlign,
            "segments:",
            segments,
            " currentHeight:",
            currentHeight
          );
        }

        let line: TextSegment[] = [];
        segments.forEach((segment) => {
          if (segment.y === currentY) {
            line.push(segment);
          } else {
            // Justifier la ligne
            const totalWidth =
              line.reduce((acc, segment) => acc + (segment.width || 0), 0) +
              currentX;

            const margin = (maxWidth - totalWidth) / (line.length - 1);
            line.forEach((segment, index) => {
              if (index > 0) {
                segment.x = (segment.x || 0) + index * margin;
              }
            });
            line = [segment];
            currentY = segment.y || 0;
            currentX = segment.x || 0;
          }
        });
      }
    }

    currentHeight += fontSize * lineSpacing;

    return {
      segments,
      height: currentHeight,
      paddingTop: wrappedLine.paddingTop,
    };
  }

  /**
   * Geouper segment non separable
   */
  groupNonSeparableSegments(segments: TextSegment[]): TextSegment[] {
    // Grouper les segments non séparables si il ont les même paramètres
    const optimizedSegments: TextSegment[] = [];
    let lastNonSeparableSegment: TextSegment | null = null;

    for (let m = 0; m < segments.length; m++) {
      const segment = segments[m];

      // Si c'est un segment non séparable et qu'il a les mêmes paramètres que le précédent
      if (
        segment.nonSeparable &&
        lastNonSeparableSegment &&
        lastNonSeparableSegment.nonSeparable &&
        lastNonSeparableSegment.bold === segment.bold &&
        lastNonSeparableSegment.size === segment.size &&
        lastNonSeparableSegment.italic === segment.italic &&
        lastNonSeparableSegment.graphic === segment.graphic &&
        lastNonSeparableSegment.underline === segment.underline &&
        lastNonSeparableSegment.link === segment.link
      ) {
        // Fusionner avec le segment précédent
        lastNonSeparableSegment.text += segment.text;
        lastNonSeparableSegment.width =
          (lastNonSeparableSegment?.width ?? 0) + (segment?.width ?? 0);
      } else {
        // Nouveau segment ou segment différent
        if (lastNonSeparableSegment) {
          optimizedSegments.push(lastNonSeparableSegment);
        }

        if (segment.nonSeparable) {
          lastNonSeparableSegment = { ...segment };
        } else {
          lastNonSeparableSegment = null;
          optimizedSegments.push(segment);
        }
      }
    }

    // Ajouter le dernier segment non séparable s'il existe
    if (lastNonSeparableSegment) {
      optimizedSegments.push(lastNonSeparableSegment);
    }

    return optimizedSegments;
  }

  /**
   * Parse le formatage Markdown dans le texte avec gestion des formatages imbriqués
   */
  parseTextFormatting(text: string, withMerge: boolean = true): TextSegment[] {
    const segments: TextSegment[] = [];

    if (this.trace) {
      console.log("parseTextFormatting input:", text);
    }
    this.groupNonSeparableSegments;
    // Traiter le texte caractère par caractère
    let i = 0;
    let currentSegment = "";
    let state = {
      bold: false,
      italic: false,
      graphic: false,
      underline: false,
      nonSeparable: false,
      link: false,
      linkId: "",
    };

    let nonSeparableBrackets = 0;

    const addSegment = (
      text: string,
      bold: boolean = false,
      italic: boolean = false,
      graphic: boolean = false,
      underline: boolean = false,
      nonSeparable: boolean = false,
      link: boolean = false,
      linkId: string | null = null
    ): void => {
      if (text) {
        segments.push({
          text,
          bold,
          italic,
          graphic,
          underline,
          nonSeparable,
          link,
          linkId: link ? linkId : "",
        });
      }
    };

    const flushCurrentSegment = (info: string = "?"): void => {
      if (currentSegment) {
        addSegment(
          currentSegment,
          state.bold,
          state.italic,
          state.graphic,
          state.underline,
          state.nonSeparable,
          state.link,
          state.linkId
        );
        currentSegment = "";
      }
    };

    let withNonSeparable = false;
    while (i < text.length) {
      const char = text[i];
      const nextChar = text[i + 1];
      const prevChar = i > 0 ? text[i - 1] : "";

      if (char === "\\" && (nextChar === "*" || nextChar === "_")) {
        currentSegment += nextChar;
        i += 2;
        continue;
      }

      // Gérer **texte** (gras)
      if (char === "*" && nextChar === "*") {
        flushCurrentSegment("bold");
        state.bold = !state.bold;
        i += 2;
        continue;
      }

      // Gérer *texte* (italique)
      if (char === "*" && nextChar !== "*" && prevChar !== "*") {
        flushCurrentSegment("italic");
        state.italic = !state.italic;
        i++;
        continue;
      }

      if (char === "`" && nextChar === "`") {
        flushCurrentSegment("graphic");
        state.graphic = !state.graphic;
        i += 2;
        continue;
      }

      // Gérer _texte_ (souligné)
      if (char === "_") {
        flushCurrentSegment("underline");
        state.underline = !state.underline;
        i++;
        continue;
      }

      // Gérer les textes non séparables
      if (char === "{") {
        withNonSeparable = true;
        state.nonSeparable = true;
        nonSeparableBrackets++;
        i++;
        continue;
      }

      if (char === "}") {
        nonSeparableBrackets--;
        if (nonSeparableBrackets === 0) {
          flushCurrentSegment("nonSeparable END");
          state.nonSeparable = false;
          state.link = false;
          state.linkId = "";
        }
        i++;
        continue;
      }
      // gerer les liens dans le texte non séparable
      if (state.nonSeparable && char === "|") {
        state.link = true;

        if (currentSegment[0] == "(") {
          const linkId = currentSegment.substring(1);
          const link = state.link;
          state.link = false;
          currentSegment = currentSegment.substring(0, 1);
          flushCurrentSegment("before link");
          state.linkId = linkId;
          state.link = link;
        } else {
          state.linkId = currentSegment;
          currentSegment = "";
        }

        i++;
        continue;
      }

      // Ajouter le caractère au segment actuel
      currentSegment += char;

      // Séparer tous les mots
      if (char === " ") {
        if (!state.nonSeparable) {
          flushCurrentSegment("space");
        }
      }

      i++;
    }

    // Ajouter le dernier segment
    flushCurrentSegment("end");

    //
    const finalSegments = segments.filter(
      (segment) => segment.text && segment.text.length > 0
    );
    if (!withMerge) {
      return finalSegments;
    }

    if (withNonSeparable) {
      // Grouper les segments non séparables si il ont les même paramètres
      return this.groupNonSeparableSegments(finalSegments);
    }

    if (this.trace) {
      console.log("parseTextFormatting output segments:", finalSegments);
    }

    return finalSegments;
  }
}

export default TextParser;
